import { ApolloProvider, useReactiveVar } from '@apollo/client';
import { MockedProvider } from '@apollo/client/testing';
import { fetchAuthSession } from '@aws-amplify/auth';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import dayjs from 'dayjs';
import getApolloClient, { cache } from 'environment/ApolloClientVMS';
import { loginRequest } from 'environment/SsoConfig';
import adminMocks from 'mocks/admin/mocks';
import nonAdminMocks from 'mocks/nonAdmin/mocks';
import mocks from 'mocks/unitTests/mocks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { signOutAction } from 'state/actions/auth';
import { setLoadingTokens, setTokenType, untriggerFetchToken } from 'state/actions/graphqlToken';
import { deriveTokenType } from 'state/deriveTokenType';
import { graphqlTokenVar, tenantsServiceTokenVar } from 'state/vars';

const isMockedMode = process.env.REACT_APP_MODE === 'mocked';
const isMockedNonAdminMode = process.env.REACT_APP_MODE === 'mockedNonAdmin';
const isMockedAdminMode = process.env.REACT_APP_MODE === 'mockedAdmin';

export const getMocks = () => {
  if (isMockedMode) return mocks;
  if (isMockedNonAdminMode) return nonAdminMocks;
  if (isMockedAdminMode) return adminMocks;

  return undefined;
};

const ONE_MINUTE = 60000;

const noTokenPromise = Promise.resolve({
  accessToken: 'no-token'
} as AuthenticationResult);

const getTokenPromise = (loggedAccount: AccountInfo, instance: IPublicClientApplication) =>
  instance
    .acquireTokenSilent({
      ...loginRequest,
      account: loggedAccount
    })
    .catch((error: unknown) => {
      localStorage.clear();

      if (error instanceof InteractionRequiredAuthError) {
        console.error(error);

        // Assertion needed to avoid a TypeScript error.
        // acquireTokenRedirect redirects the user to the Microsoft login page.
        return instance.acquireTokenRedirect(loginRequest) as unknown as AuthenticationResult;
      }

      return noTokenPromise;
    });

const Apollo: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { tokenType, isTriggerFetchToken } = useReactiveVar(graphqlTokenVar);

  const { accounts, inProgress, instance } = useMsal();
  const [loggedAccount] = accounts;

  const tenantRegistrationToken = useReactiveVar(tenantsServiceTokenVar);

  const [entraToken, setEntraToken] = useState<string>();
  const [entraTokenExpTime, setEntraTokenExpTime] = useState<dayjs.Dayjs>();
  const [isEntraTokenLoading, setIsEntraTokenLoading] = useState<boolean>(true);

  const [cognitoToken, setCognitoToken] = useState<string>();
  const [cognitoTokenExpTime, setCognitoTokenExpTime] = useState<dayjs.Dayjs>();
  const [isCognitoTokenLoading, setIsCognitoTokenLoading] = useState<boolean>(true);
  const lastCognitoExpirationRef = useRef<number | undefined>(undefined);

  const isEntraFetchInProgressRef = useRef(false);
  const isCognitoFetchInProgressRef = useRef(false);
  const tokenRef = useRef('');

  const jwt = new URLSearchParams(window.location.search).get('id');

  useEffect(() => {
    tenantsServiceTokenVar(jwt);

    return () => {
      tenantsServiceTokenVar(null);
    };
  }, [jwt]);

  const fetchEntraToken = async () => {
    if (isEntraFetchInProgressRef.current) return;

    isEntraFetchInProgressRef.current = true;

    try {
      setIsEntraTokenLoading(true);

      if (!loggedAccount) {
        setEntraToken(undefined);
        setEntraTokenExpTime(undefined);
        return;
      }

      const result = await getTokenPromise(loggedAccount, instance);
      const token = result.accessToken;

      if (!token) {
        setEntraToken(undefined);
        setEntraTokenExpTime(undefined);

        signOutAction();
        await instance.logout();
        return;
      }

      if (token === 'no-token') {
        setEntraToken(undefined);
        setEntraTokenExpTime(undefined);
        return;
      }

      setEntraToken(token);
      setEntraTokenExpTime(result.expiresOn ? dayjs(result.expiresOn) : undefined);
    } catch (error) {
      console.error('Error while fetching Entra token', error);
      setEntraToken(undefined);
      setEntraTokenExpTime(undefined);
    } finally {
      isEntraFetchInProgressRef.current = false;
      setIsEntraTokenLoading(false);
    }
  };

  const getCognitoToken = async (forceRefresh = false) => {
    if (isCognitoFetchInProgressRef.current) return;

    isCognitoFetchInProgressRef.current = true;

    try {
      setIsCognitoTokenLoading(true);

      const session = await fetchAuthSession({ forceRefresh });
      const accessToken = session.tokens?.accessToken;

      if (accessToken) {
        const expirationTime = accessToken.payload.exp;

        // fetchAuthSession() can hand back the same already-expired token when the refresh token
        // itself is dead. Without this guard, checkCognitoTokenExpiry keeps calling this on every
        // effect run, which sets state every time and re-triggers the effect — infinite loop.
        if (
          expirationTime &&
          expirationTime === lastCognitoExpirationRef.current &&
          dayjs.unix(expirationTime).isBefore(dayjs())
        ) {
          setCognitoToken(undefined);
          setCognitoTokenExpTime(undefined);
          signOutAction();
          return;
        }

        lastCognitoExpirationRef.current = expirationTime;
        setCognitoToken('' + accessToken);
        if (expirationTime) {
          setCognitoTokenExpTime(dayjs.unix(expirationTime));
        } else {
          setCognitoTokenExpTime(undefined);
        }
      } else {
        setCognitoToken(undefined);
        setCognitoTokenExpTime(undefined);
      }
    } catch (error) {
      // By setting the token information to undefined, if there is no other
      // available token, the app will eventually enter a failed
      // authentication state and force a new login.
      console.error('Error while fetching Cognito token', error);
      setCognitoToken(undefined);
      setCognitoTokenExpTime(undefined);
    } finally {
      isCognitoFetchInProgressRef.current = false;
      setIsCognitoTokenLoading(false);
    }
  };

  useEffect(() => {
    const checkEntraTokenExpiry = async () => {
      if (tokenType === 'ENTRA' && entraTokenExpTime && !isEntraTokenLoading && dayjs().isAfter(entraTokenExpTime)) {
        await fetchEntraToken();
      }
    };

    const checkCognitoTokenExpiry = async () => {
      if (
        tokenType === 'COGNITO' &&
        cognitoTokenExpTime &&
        !isCognitoTokenLoading &&
        dayjs().isAfter(cognitoTokenExpTime)
      ) {
        // Our own expiry check determined that the token is expired,
        // so explicitly request a refreshed session.
        await getCognitoToken(true);
      }
    };

    const checkTokenExpiry = async () => {
      await checkEntraTokenExpiry();
      await checkCognitoTokenExpiry();
    };

    void checkTokenExpiry();

    const interval = setInterval(() => {
      void checkTokenExpiry();
    }, ONE_MINUTE);

    return () => clearInterval(interval);
  }, [tokenType, cognitoTokenExpTime, entraTokenExpTime, isCognitoTokenLoading, isEntraTokenLoading]);

  // Single source of truth for tokenType: it is derived from whichever
  // credential exists and published in exactly one place. No other code path
  // writes tokenType, so asynchronous token fetches cannot overwrite each
  // other's result.
  useEffect(() => {
    if (tenantRegistrationToken) {
      // Onboarding uses the URL JWT directly. There is no Cognito or Entra
      // request to wait for, so the trigger must also be cleared; otherwise,
      // getIsLoading() remains true and the app stays on the loader.
      untriggerFetchToken();
      setTokenType('TENANT');
      return;
    }

    // Wait for MSAL to finish any interaction before deciding. The Entra
    // account may not yet be available while redirect handling is in progress.
    if (inProgress !== 'none') return;

    if (isTriggerFetchToken) {
      setLoadingTokens();
      untriggerFetchToken();

      void fetchEntraToken();
      void getCognitoToken();
      return;
    }

    // Publish the result only after both token fetchers have settled, so the
    // result is deterministic rather than decided by whichever request
    // finishes last.
    if (!isEntraTokenLoading && !isCognitoTokenLoading) {
      setTokenType(
        deriveTokenType({
          cognitoToken,
          entraToken
        })
      );
    }
  }, [
    tenantRegistrationToken,
    isTriggerFetchToken,
    inProgress,
    loggedAccount,
    isCognitoTokenLoading,
    isEntraTokenLoading,
    cognitoToken,
    entraToken
  ]);

  const getApolloProviderToken = () => {
    switch (tokenType) {
      case 'COGNITO':
        return cognitoToken;

      case 'ENTRA':
        return entraToken;

      case 'TENANT':
        return tenantRegistrationToken;

      default:
        return undefined;
    }
  };

  // Keep the latest token in a ref so the Apollo link can read it for every
  // request without recreating the client.
  tokenRef.current = getApolloProviderToken() || '';

  const apolloClient = useMemo(() => getApolloClient(() => Promise.resolve(tokenRef.current)), []);

  if (isMockedNonAdminMode || isMockedAdminMode || isMockedMode) {
    return (
      <MockedProvider
        mocks={getMocks()}
        cache={cache}
        defaultOptions={{
          watchQuery: { fetchPolicy: 'network-only' },
          query: { fetchPolicy: 'network-only' }
        }}
      >
        {children}
      </MockedProvider>
    );
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default Apollo;
